import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, ButtonBase, Checkbox, LinearProgress, Stack, TextField, Typography } from '@mui/material';
import { alpha, darken } from '@mui/material/styles';
import { ArrowLeft, Clipboard, ListOrdered, Undo2 } from 'lucide-react';
import ToolCard, { ToolButton, toolTextSx } from './ToolCard';
import useTaskPrioritizer from '../utils/useTaskPrioritizer';
import { comparisonCount, MAX_PRIORITY_DRAFT, parsePriorityDraft, priorityListText, rankPriorityTasks } from '../utils/taskPrioritizer';

export default function TaskPrioritizer() {
  const { state, dispatch, storageUnavailable } = useTaskPrioritizer();
  const [copyStatus, setCopyStatus] = useState('');
  const [manualCopy, setManualCopy] = useState(false);
  const copyField = useRef(null);
  const firstChoice = useRef(null);
  const resultTitle = useRef(null);
  const lastChoice = useRef(null);
  const shouldFocus = useRef(false);
  const draft = useMemo(() => parsePriorityDraft(state.draft), [state.draft]);
  const ranked = useMemo(() => rankPriorityTasks(state.tasks, state.decisions), [state.tasks, state.decisions]);
  const total = comparisonCount(state.tasks.length);
  const text = priorityListText(ranked, state.checked, state.exportFormat);
  const pairId = state.pair?.join(':');
  const hasTie = ranked.some((task, index) => index > 0 && task.wins === ranked[index - 1].wins && task.tiedWins === ranked[index - 1].tiedWins);

  useEffect(() => {
    if (state.phase === 'results' && (shouldFocus.current || lastChoice.current)) resultTitle.current?.focus({ preventScroll: true });
    else if (shouldFocus.current || lastChoice.current && document.activeElement === document.body) firstChoice.current?.focus({ preventScroll: true });
    shouldFocus.current = false;
    lastChoice.current = null;
  }, [pairId, state.phase]);
  useEffect(() => { setCopyStatus(''); setManualCopy(false); }, [state.phase]);
  useEffect(() => {
    if (manualCopy) { copyField.current?.focus(); copyField.current?.select(); }
  }, [manualCopy]);

  const send = (action) => { setCopyStatus(''); dispatch(action); };
  const copyList = async () => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(text);
      setManualCopy(false);
      setCopyStatus('Copied.');
    } catch (_error) {
      setCopyStatus('Select and copy the list below.');
      setManualCopy(true);
    }
  };

  return (
    <ToolCard id="task-prioritizer" title="Task prioritizer" icon={<ListOrdered size={22} />}>
      {storageUnavailable && <Alert severity="info" sx={{ mb: 2, '& .MuiAlert-message': toolTextSx }}>
        This browser can’t save your list. Keep this page open to keep your progress.
      </Alert>}
      {state.phase === 'edit' && (
        <>
          <TextField label="Your to-do list" multiline minRows={4} maxRows={10} fullWidth value={state.draft}
            placeholder={'Send the email\nBook an appointment\nStart the laundry'}
            onChange={(event) => send({ type: 'draft', value: event.target.value })}
            inputProps={{ maxLength: MAX_PRIORITY_DRAFT }} error={Boolean(draft.error)}
            helperText={draft.error || 'One task per line, up to 50 tasks. Duplicates are combined.'}
            InputLabelProps={{ sx: { fontSize: { xs: '0.65rem', sm: '0.7rem' } } }}
            FormHelperTextProps={{ sx: { ...toolTextSx, mx: 0, color: draft.error ? 'secondary.main' : 'text.primary' } }}
            sx={{ mt: 1, '& textarea': { fontSize: '0.75rem', lineHeight: 2 }, '& .MuiOutlinedInput-root': { borderRadius: 3 } }} />
          {draft.titles.length > 0 && <Typography sx={{ ...toolTextSx, mt: 2, color: 'secondary.main' }}>
            {draft.titles.length} {draft.titles.length === 1 ? 'task' : 'tasks'} · {comparisonCount(draft.titles.length)} comparisons
          </Typography>}
          {state.tasks.length > 0 && <Typography sx={{ ...toolTextSx, mt: 2, color: 'text.primary' }}>Prioritizing again starts a new round.</Typography>}
          <Stack direction="row" flexWrap="wrap" gap={2} sx={{ mt: 3 }}>
            <ToolButton variant="contained" disabled={!draft.titles.length || Boolean(draft.error)} onClick={() => {
              shouldFocus.current = true; send({ type: 'start' });
            }}>Prioritize</ToolButton>
            {state.tasks.length > 0 && <ToolButton onClick={() => send({ type: 'cancelEdit' })}>Cancel editing</ToolButton>}
          </Stack>
        </>
      )}
      {state.phase === 'compare' && (
        <>
          <Typography component="h3" sx={{ fontSize: { xs: '0.8rem', sm: '1rem' }, lineHeight: 1.9, textAlign: 'center', mt: 1, mb: 2 }}>Which is more important?</Typography>
          <Typography role="status" sx={{ ...toolTextSx, color: 'secondary.main', mb: 1.5 }}>
            {state.decisions.length} of {total} comparisons
          </Typography>
          <LinearProgress variant="determinate" value={state.decisions.length / total * 100} aria-label="Prioritizing progress" sx={{ height: 6, borderRadius: 4, mb: 3 }} />
          <Box role="group" aria-label="Task comparison" sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: { xs: 1.5, sm: 3 } }}>
            {state.pair.map((id, index) => {
              const task = state.tasks.find((item) => item.id === id);
              return <ButtonBase key={id} ref={index === 0 ? firstChoice : undefined} onClick={() => {
                lastChoice.current = id;
                send({ type: 'choose', pair: [...state.pair], winner: id });
              }} sx={(theme) => ({
                minWidth: 0, minHeight: { xs: 140, sm: 170 }, p: { xs: 2, sm: 3 }, borderRadius: 5,
                border: `3px solid ${theme.palette.green.main}`, color: theme.palette.text.primary,
                background: `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.1)}, ${theme.palette.background.default})`,
                boxShadow: `0 5px 0 ${darken(theme.palette.green.main, 0.65)}, 0 0 14px ${alpha(theme.palette.green.main, 0.2)}`, transition: 'transform 140ms ease, box-shadow 140ms ease',
                '@keyframes priorityChoiceIn': { from: { opacity: 0, transform: 'translateY(6px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
                animation: 'priorityChoiceIn 160ms ease-out',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 7px 0 ${darken(theme.palette.green.main, 0.65)}, 0 0 20px ${alpha(theme.palette.green.main, 0.3)}` },
                '&:active': { transform: 'translateY(3px)', boxShadow: 'none' },
                '&:focus-visible': { outline: `3px solid ${theme.palette.secondary.main}`, outlineOffset: 4 },
                '@media (prefers-reduced-motion: reduce)': { animation: 'none', transition: 'none' },
              })}>
                <Typography component="span" sx={{ ...toolTextSx, overflowWrap: 'anywhere', textAlign: 'center' }}>{task.title}</Typography>
              </ButtonBase>;
            })}
          </Box>
          <Stack direction="row" flexWrap="wrap" gap={2} sx={{ mt: 3 }}>
            <ToolButton startIcon={<Undo2 size={17} />} disabled={!state.decisions.length} sx={{ '&.Mui-disabled': { color: 'primary.main' } }} onClick={() => {
              shouldFocus.current = true; send({ type: 'undo' });
            }}>Undo choice</ToolButton>
            <ToolButton onClick={() => send({ type: 'edit' })}>Edit list</ToolButton>
          </Stack>
        </>
      )}
      {state.phase === 'results' && (
        <>
          <Typography component="h3" ref={resultTitle} tabIndex={-1} sx={{ fontSize: { xs: '0.85rem', sm: '1rem' }, lineHeight: 1.9, textAlign: 'center', m: 0, outline: 'none' }}>Your prioritized list</Typography>
          <Typography sx={{ ...toolTextSx, color: state.checked.length === state.tasks.length ? 'green.main' : 'secondary.main', mt: 1 }}>
            {state.checked.length} of {state.tasks.length} done
          </Typography>
          <Box component="ol" aria-label="Prioritized tasks" sx={{ listStyle: 'none', p: 0, my: 2 }}>
            {ranked.map((task, index) => (
              <Stack component="li" key={task.id} direction="row" alignItems="center" gap={1} sx={{ py: 0.5 }}>
                <Typography aria-hidden="true" sx={{ ...toolTextSx, minWidth: 28 }}>{index + 1}.</Typography>
                <Box component="label" sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, cursor: 'pointer' }}>
                  <Checkbox checked={state.checked.includes(task.id)} onChange={() => send({ type: 'check', id: task.id })}
                    inputProps={{ 'aria-label': task.title }} sx={{ color: 'primary.main', '&.Mui-checked': { color: 'green.main' } }} />
                  <Typography component="span" sx={{ ...toolTextSx, overflowWrap: 'anywhere', textDecoration: state.checked.includes(task.id) ? 'line-through' : 'none', opacity: state.checked.includes(task.id) ? 0.6 : 1 }}>{task.title}</Typography>
                </Box>
              </Stack>
            ))}
          </Box>
          {hasTie && <Typography sx={{ ...toolTextSx, mb: 2, color: 'text.primary' }}>Any remaining ties keep their original order.</Typography>}
          <Stack direction="row" flexWrap="wrap" alignItems="center" gap={2} sx={{ mt: 3 }}>
            <TextField select label="Copy as" value={state.exportFormat} onChange={(event) => send({ type: 'format', value: event.target.value })}
              SelectProps={{ native: true }} InputLabelProps={{ sx: { fontSize: { xs: '0.65rem', sm: '0.65rem' } } }}
              sx={{ minWidth: 180, '& select': { fontSize: '0.7rem' } }}>
              <option value="checklist">Checklist</option><option value="numbered">Numbered list</option>
            </TextField>
            <ToolButton variant="contained" startIcon={<Clipboard size={17} />} onClick={copyList}>Copy list</ToolButton>
          </Stack>
          {copyStatus && <Typography role="status" sx={{ ...toolTextSx, mt: 2, color: 'green.main' }}>{copyStatus}</Typography>}
          {manualCopy && <TextField label="Copy your list" inputRef={copyField} value={text} multiline minRows={3} maxRows={8}
            fullWidth InputProps={{ readOnly: true }} inputProps={{ 'aria-label': 'Prioritized list to copy' }}
            InputLabelProps={{ sx: { fontSize: { xs: '0.65rem', sm: '0.65rem' } } }} sx={{ mt: 2, '& textarea': { fontSize: '0.75rem', lineHeight: 2 } }} />}
          <Stack direction="row" flexWrap="wrap" gap={2} sx={{ mt: 3 }}>
            <ToolButton onClick={() => send({ type: 'edit' })} startIcon={<ArrowLeft size={17} />}>Edit list</ToolButton>
            {state.decisions.length > 0 && <ToolButton onClick={() => {
              shouldFocus.current = true; send({ type: 'undo' });
            }}>Undo choice</ToolButton>}
          </Stack>
          <Box component="details" sx={{ ...toolTextSx, mt: 3 }}>
            <Box component="summary" sx={{ cursor: 'pointer', color: 'secondary.main' }}>How this is ranked</Box>
            <Typography sx={{ ...toolTextSx, mt: 2 }}>Every pair is compared once. More wins ranks a task higher. Ties use the choices within that group, then the original list order.</Typography>
          </Box>
        </>
      )}
    </ToolCard>
  );
}
